import React, { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import { CtyButton, CtySpinner } from '@circuitry-ai/doc-ui';
import type { ColDef, ICellRendererParams } from 'ag-grid-community';
import { HorizontalRulesAgGrid } from './EditResourceAccessModal';
import { Select, SelectItem } from '@nextui-org/react';
import { 
  useGetFgaAllResourcesQuery,
  useGetFgaGroupResourcesQuery,
  useGetFgaGroupsQuery,
  useAddFgaGroupMutation,
  useAddFgaEntityResourceMutation,
  useRemoveFgaEntityResourceMutation,
  useAddFgaApplicationResourceMutation,
  useRemoveFgaApplicationResourceMutation,
  useAddFgaEntityTypeMutation,
  useRemoveFgaEntityTypeMutation
} from '../../../../shared/data/src/features/settings/settingsApiSlice';
import { handleApiError, handleApiSuccess } from '../fga-utils';
import { applyApplicationResourceToggle, orderApplicationResources } from './expert-answer-permissions';

export interface GroupModalProps {
  readonly isOpen: boolean;
  readonly onOpenChange: (isOpen: boolean) => void;
  readonly mode: 'create' | 'edit';
  readonly groupName?: string;
}

type PermissionLevel = 'read' | 'update' | 'delete';
type ResourceTypeFilter = 'all' | 'application_resource' | 'entity_type' | 'entity_resource';

interface ApplicationResourceState {
  resource: string;
  resourceId: string;
  hasView: boolean;
}

interface EntityTypeState {
  entityType: string;
  hasCreateInstance: boolean;
}

interface EntityResourceState {
  resource: string;
  resourceId: string;
  permissions: PermissionLevel[];
  entityType: string;
  readManuallySelected?: boolean; // Track if read was manually selected by user
}

const PERMISSION_LEVELS = [
  { value: 'read', label: 'Read', description: 'Read-only access' },
  { value: 'update', label: 'Update', description: 'Configure and edit' },
  { value: 'delete', label: 'Delete', description: 'Delete Access' }
] as const;

type GroupToggleCtx = { onToggle: (id: string, value: boolean) => void };

const GroupAppResourcePermissionCell: React.FC<ICellRendererParams<ApplicationResourceState>> = (params) => {
  const d = params.data;
  const ctx = params.context as GroupToggleCtx | undefined;
  if (!d || !ctx) return null;
  return (
    <label className="flex items-center gap-2 cursor-pointer" htmlFor={`app-resource-${d.resourceId}`} aria-label={`${d.resource} - View permission`}>
      <input
        id={`app-resource-${d.resourceId}`}
        type="checkbox"
        checked={d.hasView}
        onChange={(e) => ctx.onToggle(d.resourceId, e.target.checked)}
        className="w-4 h-4 text-primary border-default-300 rounded focus:ring-primary"
      />
      <div>
        <div className="text-sm font-medium text-foreground">View</div>
        <div className="text-xs text-default-500">View access</div>
      </div>
    </label>
  );
};

const GroupEntityTypePermissionCell: React.FC<ICellRendererParams<EntityTypeState>> = (params) => {
  const d = params.data;
  const ctx = params.context as GroupToggleCtx | undefined;
  if (!d || !ctx) return null;
  return (
    <label className="flex items-center gap-2 cursor-pointer" htmlFor={`entity-type-${d.entityType}`} aria-label={`${d.entityType} - Create Instance permission`}>
      <input
        id={`entity-type-${d.entityType}`}
        type="checkbox"
        checked={d.hasCreateInstance}
        onChange={(e) => ctx.onToggle(d.entityType, e.target.checked)}
        className="w-4 h-4 text-primary border-default-300 rounded focus:ring-primary"
      />
      <div>
        <div className="text-sm font-medium text-foreground">Create Instance</div>
        <div className="text-xs text-default-500">Create instance access</div>
      </div>
    </label>
  );
};

type GroupEntityResourceGridCtx = { onPermissionChange: (resourceId: string, permission: PermissionLevel) => void };

const GroupEntityResourcePermissionsCell: React.FC<ICellRendererParams<EntityResourceState>> = (params) => {
  const er = params.data;
  const ctx = params.context as GroupEntityResourceGridCtx | undefined;
  if (!er || !ctx) return null;
  return (
    <div className="flex flex-wrap gap-6">
      {PERMISSION_LEVELS.map((level) => {
        const isRead = level.value === 'read';
        const hasUpdateOrDelete = er.permissions.includes('update') || er.permissions.includes('delete');
        const isReadDisabled = isRead && hasUpdateOrDelete;
        return (
          <label
            key={level.value}
            className={`flex items-center gap-2 ${isReadDisabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
            htmlFor={`permission-${er.resourceId}-${level.value}`}
            aria-label={`${er.resource} - ${level.label} permission`}
          >
            <input
              id={`permission-${er.resourceId}-${level.value}`}
              type="checkbox"
              value={level.value}
              checked={er.permissions.includes(level.value)}
              aria-disabled={isReadDisabled}
              className={`w-4 h-4 text-primary border-default-300 rounded focus:ring-primary ${
                isReadDisabled ? 'bg-primary/10 cursor-not-allowed opacity-70' : 'cursor-pointer'
              }`}
              onChange={() => {
                if (isReadDisabled) return;
                ctx.onPermissionChange(er.resourceId, level.value);
              }}
            />
            <div className={isReadDisabled ? 'opacity-70' : ''}>
              <div className="text-sm font-medium text-foreground">{level.label}</div>
              <div className="text-xs text-default-500">{level.description}</div>
            </div>
          </label>
        );
      })}
    </div>
  );
};

const GROUP_APP_RESOURCE_COL_DEFS: ColDef<ApplicationResourceState>[] = [
  {
    field: 'resource',
    headerName: 'Resource',
    flex: 1,
    minWidth: 160,
    cellClass: 'font-medium text-foreground',
  },
  {
    colId: 'permission',
    headerName: 'Permission Level',
    flex: 1,
    minWidth: 200,
    cellRenderer: GroupAppResourcePermissionCell,
  },
];

const GROUP_ENTITY_TYPE_COL_DEFS: ColDef<EntityTypeState>[] = [
  {
    field: 'entityType',
    headerName: 'Resource',
    flex: 1,
    minWidth: 160,
    cellClass: 'font-medium text-foreground',
  },
  {
    colId: 'permission',
    headerName: 'Permission Level',
    flex: 1,
    minWidth: 200,
    cellRenderer: GroupEntityTypePermissionCell,
  },
];

const GROUP_ENTITY_RESOURCE_COL_DEFS: ColDef<EntityResourceState>[] = [
  {
    field: 'resource',
    headerName: 'Resource',
    flex: 1,
    minWidth: 160,
    cellClass: 'font-medium text-foreground',
  },
  {
    colId: 'permissions',
    headerName: 'Permission Level',
    flex: 1,
    minWidth: 360,
    cellRenderer: GroupEntityResourcePermissionsCell,
  },
];

export function GroupModal({ isOpen, onOpenChange, mode, groupName }: GroupModalProps) {
  const [groupNameInput, setGroupNameInput] = useState('');
  const [isSaveAsMode, setIsSaveAsMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resourceTypeFilter, setResourceTypeFilter] = useState<ResourceTypeFilter>('all');
  const [entityTypeFilter, setEntityTypeFilter] = useState<string>('all');
  const [roleNameError, setRoleNameError] = useState<string>('');
  
  // Application Resources state
  const [applicationResources, setApplicationResources] = useState<ApplicationResourceState[]>([]);
  const [originalApplicationResources, setOriginalApplicationResources] = useState<Set<string>>(new Set());
  
  // Entity Types state
  const [entityTypes, setEntityTypes] = useState<EntityTypeState[]>([]);
  const [originalEntityTypes, setOriginalEntityTypes] = useState<Set<string>>(new Set());
  
  // Entity Resources state
  const [entityResources, setEntityResources] = useState<EntityResourceState[]>([]);
  const [originalEntityResources, setOriginalEntityResources] = useState<Map<string, PermissionLevel[]>>(new Map());

  const { data: allResourcesData, isFetching: allResourcesFetching, refetch: refetchAllResources } = useGetFgaAllResourcesQuery();
  const { data: groupResourcesData, isFetching: groupResourcesFetching, refetch: refetchGroupResources } = useGetFgaGroupResourcesQuery(
    groupName || '', 
    { skip: !isOpen || mode !== 'edit' || !groupName }
  );
  const { refetch: refetchFgaGroups } = useGetFgaGroupsQuery();
  const [addFgaGroup] = useAddFgaGroupMutation();
  const [addFgaEntityResource] = useAddFgaEntityResourceMutation();
  const [removeFgaEntityResource] = useRemoveFgaEntityResourceMutation();
  const [addFgaApplicationResource] = useAddFgaApplicationResourceMutation();
  const [removeFgaApplicationResource] = useRemoveFgaApplicationResourceMutation();
  const [addFgaEntityType] = useAddFgaEntityTypeMutation();
  const [removeFgaEntityType] = useRemoveFgaEntityTypeMutation();

  const isCreatingRole = mode === 'create' || isSaveAsMode;

  // Initialize data
  useEffect(() => {
    if (!isOpen) {
      setGroupNameInput('');
      setIsSaveAsMode(false);
      setApplicationResources([]);
      setEntityTypes([]);
      setEntityResources([]);
      setOriginalApplicationResources(new Set());
      setOriginalEntityTypes(new Set());
      setOriginalEntityResources(new Map());
      setResourceTypeFilter('all');
      setEntityTypeFilter('all');
      setRoleNameError('');
      return;
    }

    refetchAllResources();
    if (mode === 'edit' && groupName && !isSaveAsMode) {
      setGroupNameInput(groupName);
      refetchGroupResources();
    } else if (mode === 'create') {
      setGroupNameInput('');
    }
  }, [isOpen, mode, groupName, isSaveAsMode, refetchGroupResources, refetchAllResources]);

  // Load all resources and group resources
  useEffect(() => {
    if (!allResourcesData?.data?.resources) return;

    const resources = allResourcesData.data.resources;
    
    // Initialize Application Resources from all resources
    const appResources: ApplicationResourceState[] = (resources.application_resources || []).map((r: any) => ({
      resource: r.resource_name || r.application_resource,
      resourceId: r.application_resource,
      hasView: false
    }));
    
    // Initialize Entity Types from all resources
    const entityTypesList: EntityTypeState[] = (resources.entity_types || []).map((r: any) => ({
      entityType: r.entity_type,
      hasCreateInstance: false
    }));
    
    // Initialize Entity Resources from all resources
    const entityResourcesList: EntityResourceState[] = [];
    const entityResourceMap = new Map<string, EntityResourceState>();
    
    (resources.entity_resources || []).forEach((r: any) => {
      const entityResourceId = r.entity_resource;
      if (!entityResourceId || entityResourceMap.has(entityResourceId)) return;
      entityResourceMap.set(entityResourceId, {
        resource: r.resource_name || entityResourceId,
        resourceId: entityResourceId,
        permissions: [],
        entityType: r.entity_type || '',
        readManuallySelected: false
      });
    });
    
    entityResourcesList.push(...Array.from(entityResourceMap.values()));

    if (isSaveAsMode) {
      return;
    }

    // Update with group resources data if in edit mode
    if (mode === 'edit' && groupResourcesData?.data?.resources) {
      const groupResources = groupResourcesData.data.resources;
      
      // Update Application Resources
      const assignedAppResources = new Set<string>();
      (groupResources.application_resources || []).forEach((r: any) => {
        assignedAppResources.add(r.application_resource);
      });
      
      setApplicationResources(orderApplicationResources(appResources.map(ar => ({
        ...ar,
        hasView: assignedAppResources.has(ar.resourceId)
      }))));
      setOriginalApplicationResources(assignedAppResources);
      
      // Update Entity Types
      const assignedEntityTypes = new Set<string>();
      (groupResources.entity_types || []).forEach((r: any) => {
        assignedEntityTypes.add(r.entity_type);
      });
      
      setEntityTypes(entityTypesList.map(et => ({
        ...et,
        hasCreateInstance: assignedEntityTypes.has(et.entityType)
      })));
      setOriginalEntityTypes(assignedEntityTypes);
      
      // Update Entity Resources
      const entityResourcePermissions = new Map<string, PermissionLevel[]>();
      (groupResources.entity_resources || []).forEach((r: any) => {
        const entityResourceId = r.entity_resource;
        if (!entityResourceId) return;
        const relation = r.relation as PermissionLevel;
        if (!entityResourcePermissions.has(entityResourceId)) {
          entityResourcePermissions.set(entityResourceId, []);
        }
        const perms = entityResourcePermissions.get(entityResourceId)!;
        if (!perms.includes(relation)) {
          perms.push(relation);
        }
      });
      
      setEntityResources(entityResourcesList.map(er => ({
        ...er,
        permissions: entityResourcePermissions.get(er.resourceId) || [],
        readManuallySelected: (entityResourcePermissions.get(er.resourceId) || []).includes('read')
      })));
      setOriginalEntityResources(entityResourcePermissions);
    } else {
      // Create mode - initialize with empty states
      setApplicationResources(orderApplicationResources(appResources));
      setEntityTypes(entityTypesList);
      setEntityResources(entityResourcesList);
    }
  }, [allResourcesData, groupResourcesData, mode, isSaveAsMode, isOpen]);

  const handleSaveAs = () => {
    setGroupNameInput('');
    setRoleNameError('');
    setOriginalApplicationResources(new Set());
    setOriginalEntityTypes(new Set());
    setOriginalEntityResources(new Map());
    setIsSaveAsMode(true);
  };
  const handleApplicationResourceToggle = (resourceId: string, hasView: boolean) => {
    setApplicationResources(prev => applyApplicationResourceToggle(prev, resourceId, hasView));
  };

  // Handle Entity Type toggle
  const handleEntityTypeToggle = (entityType: string, hasCreateInstance: boolean) => {
    setEntityTypes(prev => 
      prev.map(et => et.entityType === entityType ? { ...et, hasCreateInstance } : et)
    );
  };

  // Handle Entity Resource permission change
  const handleEntityResourcePermissionChange = (resourceId: string, permission: PermissionLevel) => {
    setEntityResources(prev => {
      const updatedResources = prev.map(er => {
        if (er.resourceId !== resourceId) return er;
        const hasPermission = er.permissions.includes(permission);
        let newPermissions = hasPermission
          ? er.permissions.filter(p => p !== permission)
          : [...er.permissions, permission];
        let readManuallySelected = er.readManuallySelected ?? false;
        // If user is directly selecting/deselecting read, track it as manual
        if (permission === 'read') {
          readManuallySelected = !hasPermission; // true if selecting, false if deselecting
        }
        
        // Auto-select read permission when update or delete is selected
        if ((permission === 'update' || permission === 'delete') && !hasPermission) {
          // Adding update or delete - ensure read is included
          if (!newPermissions.includes('read')) {
            newPermissions = [...newPermissions, 'read'];
            // Mark as auto-selected if read wasn't already there
            if (!er.permissions.includes('read')) {
              readManuallySelected = false;
            }
          }
        }
        
        // When removing update or delete, check if read should also be removed
        if ((permission === 'update' || permission === 'delete') && hasPermission) {
          // Removing update or delete
          const hasUpdate = newPermissions.includes('update');
          const hasDelete = newPermissions.includes('delete');
          
          // If neither update nor delete is selected anymore
          if (!hasUpdate && !hasDelete) {
            // Only remove read if it was NOT manually selected
            if (!readManuallySelected) {
              newPermissions = newPermissions.filter(p => p !== 'read');
            }
            // Reset the flag since update/delete are gone
            readManuallySelected = newPermissions.includes('read') && readManuallySelected;
          }
        }
        
        return { ...er, permissions: newPermissions, readManuallySelected };
      });
      return updatedResources;
    });
  };

  // Handle Application Resources changes
  const handleApplicationResourcesChanges = async (targetGroup: string) => {
    const currentAssigned = new Set(
      applicationResources.filter(ar => ar.hasView).map(ar => ar.resourceId)
    );
    
    // Remove resources that are no longer selected
    for (const originalResourceId of originalApplicationResources) {
      if (!currentAssigned.has(originalResourceId)) {
        await removeFgaApplicationResource({
          group: targetGroup,
          application_resource: originalResourceId,
          relation: 'view'
        }).unwrap();
      }
    }
    
    // Add new resources
    for (const resourceId of currentAssigned) {
      if (!originalApplicationResources.has(resourceId)) {
        await addFgaApplicationResource({
          group: targetGroup,
          application_resource: resourceId,
          relation: 'view'
        }).unwrap();
      }
    }
  };

  // Handle Entity Types changes
  const handleEntityTypesChanges = async (targetGroup: string) => {
    const currentAssigned = new Set(
      entityTypes.filter(et => et.hasCreateInstance).map(et => et.entityType)
    );
    
    // Remove entity types that are no longer selected
    for (const originalEntityType of originalEntityTypes) {
      if (!currentAssigned.has(originalEntityType)) {
        await removeFgaEntityType({
          group: targetGroup,
          entity_type: originalEntityType,
          relation: 'create_instance'
        }).unwrap();
      }
    }
    
    // Add new entity types
    for (const entityType of currentAssigned) {
      if (!originalEntityTypes.has(entityType)) {
        await addFgaEntityType({
          group: targetGroup,
          entity_type: entityType,
          relation: 'create_instance'
        }).unwrap();
      }
    }
  };

  // Handle Entity Resources changes
  const handleEntityResourcesChanges = async (targetGroup: string) => {
    for (const entityResource of entityResources) {
      const originalPermissions = originalEntityResources.get(entityResource.resourceId) || [];
      const currentPermissions = entityResource.permissions;
      
      // Remove permissions that are no longer selected
      for (const permission of originalPermissions) {
        if (!currentPermissions.includes(permission)) {
          await removeFgaEntityResource({
            group: targetGroup,
            entity_resource: entityResource.resourceId,
            relation: permission
          }).unwrap();
        }
      }
      
      // Add new permissions
      for (const permission of currentPermissions) {
        if (!originalPermissions.includes(permission)) {
          await addFgaEntityResource({
            group: targetGroup,
            entity_resource: entityResource.resourceId,
            relation: permission
          }).unwrap();
        }
      }
    }
  };

  const handleSave = async () => {
    if (isCreatingRole) {
      const trimmedName = groupNameInput.trim();
      if (!trimmedName || trimmedName.includes(' ')) {
        setRoleNameError('Role name cannot contain spaces');
        return;
      }
    }
    
    setLoading(true);
    try {
      if (isCreatingRole) {
        await addFgaGroup({ group: groupNameInput.trim() }).unwrap();
        const targetGroup = groupNameInput.trim();
        
        await handleApplicationResourcesChanges(targetGroup);
        await handleEntityTypesChanges(targetGroup);
        await handleEntityResourcesChanges(targetGroup);
      } else {
        // Edit mode
        await handleApplicationResourcesChanges(groupName!);
        await handleEntityTypesChanges(groupName!);
        await handleEntityResourcesChanges(groupName!);
      }
      
      handleApiSuccess(isCreatingRole ? 'Role created successfully' : 'Role updated successfully');
      refetchFgaGroups();
      onOpenChange(false);
    } catch (error) {
      handleApiError(error, isCreatingRole ? 'create role' : 'update role');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const isDataLoading = allResourcesFetching || (mode === 'edit' && !isSaveAsMode && groupResourcesFetching);

  const modalTitle = isSaveAsMode
    ? 'Save Role As'
    : mode === 'create'
      ? 'Create New Role'
      : 'Edit Role';

  const modalDescription = isSaveAsMode
    ? 'Enter a new role name and adjust the copied permissions before saving.'
    : mode === 'create'
      ? 'Create a new role and assign resources with specific access levels. You can add users after creating the role.'
      : 'Update the role details, assigned resources, and access permissions.';

  return (
    <>
      <button 
        className="fixed inset-0 bg-black bg-opacity-50 z-[42]" 
        onClick={() => onOpenChange(false)} 
      />

      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-content1 rounded-lg shadow-xl w-full max-w-6xl max-h-[80vh] flex flex-col">
          <div className="p-3 border-b border-default-200">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-foreground">
                  {modalTitle}
                </h2>
                <p className="text-sm text-default-500 mt-1">
                  {modalDescription}
                </p>
              </div>

              <div className="flex items-center gap-2">
                {mode === 'edit' && !isSaveAsMode ? (
                  <CtyButton
                    size="sm"
                    variant="bordered"
                    onPress={handleSaveAs}
                    color="primary"
                    isDisabled={loading || isDataLoading}
                  >
                    Save As
                  </CtyButton>
                ) : null}
                <button
                  onClick={() => onOpenChange(false)}
                  className="p-2 hover:bg-default-100 rounded-full"
                  aria-label="Close"
                >
                  <Icon icon="mdi:close" className="w-5 h-5 text-default-500" />
                </button>
              </div>
            </div>
          </div>

          {isDataLoading ? (
            <div className="flex-1 flex items-center justify-center py-16">
              <CtySpinner size="md" />
            </div>
          ) : (
          <div className="flex-1 overflow-y-auto p-3">
            <div className="mb-3">
              <label htmlFor="groupName" className="block text-sm font-medium text-default-600 mb-2">
                Role Name {isCreatingRole ? '*' : ''}
              </label>
              <input
                id="groupName"
                type="text"
                value={groupNameInput}
                onChange={(e) => {
                  if (mode === 'edit' && !isSaveAsMode) return;
                  const value = e.target.value;
                  setGroupNameInput(value);
                  // Check for any space character in the role name
                  if (value.trim().includes(' ')) {
                    setRoleNameError('Role name cannot contain spaces');
                  } else {
                    setRoleNameError('');
                  }
                }}
                placeholder={
                  isCreatingRole
                    ? 'Enter role name (e.g., Content_Managers, System_Analysts)'
                    : ''
                }
                disabled={mode === 'edit' && !isSaveAsMode}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent ${
                  roleNameError ? 'border-danger' : 'border-default-300'
                } ${mode === 'edit' && !isSaveAsMode ? 'bg-default-100 cursor-not-allowed' : ''}`}
              />
              {roleNameError && isCreatingRole && (
                <p className="text-sm text-danger mt-1">{roleNameError}</p>
              )}
            </div>

            <div className="mb-3">
              <Select
                size="md"
                selectedKeys={new Set([resourceTypeFilter])}
                onSelectionChange={(keys) => {
                  const selected = Array.from(keys)[0] as ResourceTypeFilter;
                  setResourceTypeFilter(selected || 'all');
                }}
                className="w-1/4"
                disallowEmptySelection
              >
                <SelectItem key="all" value="all">All Resources</SelectItem>
                <SelectItem key="application_resource" value="application_resource">Application Resources</SelectItem>
                <SelectItem key="entity_type" value="entity_type">Entity Types</SelectItem>
                <SelectItem key="entity_resource" value="entity_resource">Entity Resources</SelectItem>
              </Select>
            </div>

            {/* Application Resources Table */}
            {applicationResources.length > 0 && (resourceTypeFilter === 'all' || resourceTypeFilter === 'application_resource') && (
              <div className="mb-6">
                <h3 className="text-lg font-medium text-foreground mb-2">Application Resources</h3>
                <p className="text-sm text-default-500 mb-3">Manage access to application resources</p>
                <HorizontalRulesAgGrid<ApplicationResourceState>
                  rowData={applicationResources}
                  columnDefs={GROUP_APP_RESOURCE_COL_DEFS}
                  getRowId={(p) => p.data.resourceId}
                  context={{ onToggle: handleApplicationResourceToggle } satisfies GroupToggleCtx}
                />
              </div>
            )}

            {/* Entity Types Table */}
            {entityTypes.length > 0 && (resourceTypeFilter === 'all' || resourceTypeFilter === 'entity_type') && (
              <div className="mb-6">
                <h3 className="text-lg font-medium text-foreground mb-2">Entity Types</h3>
                <p className="text-sm text-default-500 mb-3">Manage access to create instances of entity types</p>
                <HorizontalRulesAgGrid<EntityTypeState>
                  rowData={entityTypes}
                  columnDefs={GROUP_ENTITY_TYPE_COL_DEFS}
                  getRowId={(p) => p.data.entityType}
                  context={{ onToggle: handleEntityTypeToggle } satisfies GroupToggleCtx}
                />
              </div>
            )}

            {/* Entity Resources Table */}
            {entityResources.length > 0 && (resourceTypeFilter === 'all' || resourceTypeFilter === 'entity_resource') && (
              <div className="mb-3">
                <h3 className="text-lg font-medium text-foreground mb-2">Entity Resources</h3>
                <p className="text-sm text-default-500 mb-3">Manage access permissions for entity resources</p>
                <div className="mb-3">
                  <Select
                    size="md"
                    selectedKeys={new Set([entityTypeFilter])}
                    onSelectionChange={(keys) => {
                      const selected = Array.from(keys)[0] as string;
                      setEntityTypeFilter(selected || 'all');
                    }}
                    className="w-1/4"
                    disallowEmptySelection
                  >
                    <SelectItem key="all" value="all">All Entity Types</SelectItem>
                    <>
                      {Array.from(new Set(entityResources.map(er => er.entityType).filter(Boolean))).map((entityType) => (
                        <SelectItem key={entityType} value={entityType} textValue={entityType}>
                          {entityType.charAt(0).toUpperCase() + entityType.slice(1)}
                        </SelectItem>
                      ))}
                    </>
                  </Select>
                </div>
                <HorizontalRulesAgGrid<EntityResourceState>
                  rowData={entityResources.filter((er) => entityTypeFilter === 'all' || er.entityType === entityTypeFilter)}
                  columnDefs={GROUP_ENTITY_RESOURCE_COL_DEFS}
                  getRowId={(p) => p.data.resourceId}
                  context={
                    { onPermissionChange: handleEntityResourcePermissionChange } satisfies GroupEntityResourceGridCtx
                  }
                />
                <div className="text-xs text-default-500 m-3">
                  <span className='font-bold'>Permission Levels:</span> Each resource can have multiple permission levels. Select one or more permissions as needed.
                </div>
              </div>
            )}
          </div>
          )}

          {!isDataLoading && (
          <div className="p-3 border-t border-default-200 bg-default-100">
            <div className="flex justify-end gap-3">
              <CtyButton
                variant="bordered"
                onPress={() => onOpenChange(false)}
                isDisabled={loading}
              >
                Cancel
              </CtyButton>
              <CtyButton
                color="primary"
                onPress={handleSave}
                isLoading={loading}
                isDisabled={
                  isCreatingRole &&
                  (!groupNameInput.trim() || !!roleNameError || groupNameInput.trim().includes(' '))
                }
              >
                {isCreatingRole ? 'Create Role' : 'Save Changes'}
              </CtyButton>
            </div>
          </div>
          )}
        </div>
      </div>
    </>
  );
}

export default GroupModal;
 